import React, { useContext, useEffect, useMemo, useState } from 'react';
import { PostEditorNewFromPostView } from './PostEditorNewFromPost.view';
import { PostStatus, useGetBitlyAccountsQuery, useGetPostQuery } from '../../../models/graphql';
import { triggerErrorToast } from '../../../helpers/triggerErrorToast';
import { mapPostToGroupPostAttributesType } from '../helpers/mapFetchedPostsToState';
import { InitialPostEditorState } from '../states/initial';
import updateImmutable from 'immutability-helper';
import { AppContext, isPostEditorContextTypeRescheduleOrDuplicateGuard } from '../../../providers/AppProvider';
import { PostEditorContextEnum, PostEditorState, PostGroupsType } from '../../../models/post-editor';
import { setPostSource } from '../helpers/setPostSource';
import { useCategoriesProfilesWithBoards } from '../../../hooks/useCategoriesProfilesWithBoards';
import { usePostEditorWorkspaceSettings } from '../../../hooks/usePostEditorWorkspaceSettings';
import {
  mapPostTikTokToTikTokProfileValues,
  SocialProfileWithBoardsAndMentionType
} from '../helpers/mapSelectedProfilesValues';
import { profilesHelpers } from '../../Settings/Profiles/Profiles.helpers';
import { MappedCategoriesType } from '../../Publish/Categories/Categories.helpers';
import { useClosePostEditor } from '../../../hooks/useClosePostEditor';

export type PostEditorNewFromPostControllerProps = {
  //
  postId: string;
};

export const PostEditorNewFromPostController = (props: PostEditorNewFromPostControllerProps): JSX.Element => {
  const { postId } = props;
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const { postEditorContext } = useContext(AppContext);
  const { closePostEditor } = useClosePostEditor();
  if (!isPostEditorContextTypeRescheduleOrDuplicateGuard(postEditorContext)) {
    setLoading(false);
    throw new Error(
      'Post Editor New from Post has been called with wrong context value: ' + JSON.stringify(postEditorContext)
    );
  }
  const [postEditorState, setPostEditorState] = useState<PostEditorState>(InitialPostEditorState());
  const { postEditorCategoriesProfiles: categoriesProfiles, error: categoriesProfilesError } =
    useCategoriesProfilesWithBoards();
  const { selectedWorkspaceId } = useContext(AppContext);
  const workspaceSettings = usePostEditorWorkspaceSettings();
  const overrideWorkspaceSettings = !!postEditorContext.overrideWorkspaceSettings;
  const onClosePostEditor = postEditorContext.onClose;

  const { data: bitlyAccounts, error: bitlyAccountsError } = useGetBitlyAccountsQuery({
    variables: {
      workspaceId: selectedWorkspaceId
    }
  });
  const { type: postEditorContextType, sourceType } = postEditorContext;
  const { data, error: postError } = useGetPostQuery({ variables: { postId } });

  const profiles: SocialProfileWithBoardsAndMentionType[] = useMemo(() => {
    const socialProfiles = categoriesProfiles?.workspace.socialProfiles || [];

    if (categoriesProfiles?.workspace.socialProfiles.length === 0) {
      setLoading(false);
    }
    return socialProfiles.map(profile => ({
      ...profile,
      mentionType: profilesHelpers.getMentionType(profile)
    }));
  }, [categoriesProfiles?.workspace.socialProfiles]);

  const categories: MappedCategoriesType[] = useMemo(
    () => categoriesProfiles?.workspace.categories || [],
    [categoriesProfiles?.workspace.categories]
  );

  useEffect(() => {
    if (postError) {
      setLoading(false);
      setError(true);
      triggerErrorToast('Error Fetching Post')(postError);
    }
  }, [postError]);

  useEffect(() => {
    if (categoriesProfilesError) {
      setLoading(false);
      setError(true);
      triggerErrorToast('Error Fetching Profiles and Categories')(categoriesProfilesError);
    }
  }, [categoriesProfilesError]);

  useEffect(() => {
    if (bitlyAccountsError) {
      setLoading(false);
      setError(true);
      triggerErrorToast('Error Fetching Bitly Accounts')(bitlyAccountsError);
    }
  }, [bitlyAccountsError]);

  useEffect(() => {
    if (error) {
      onClosePostEditor && onClosePostEditor(false);
      closePostEditor();
    }
  }, [error, closePostEditor, onClosePostEditor]);

  useEffect(() => {
    if (profiles.length && categories.length && workspaceSettings && bitlyAccounts && data) {
      const { evergreen, queuePosition, scheduledTimezone, varyMedia } = workspaceSettings;
      const profilesWithBitly = bitlyAccounts.workspace.socialProfiles;
      const defaultBitly = bitlyAccounts.workspace.settings.defaultBitly;
      const { post } = data;
      const mappedGroup = mapPostToGroupPostAttributesType(
        post,
        InitialPostEditorState().postGroups[0].groupPostAttributes,
        postEditorContextType // if we are duplicationg, we need to remove parent id
      );
      const newStatus = [PostStatus.FAILED, PostStatus.PUBLISHED].includes(post.status)
        ? PostStatus.ACTIVE
        : post.status;
      setPostEditorState(old =>
        // check if we have initial (context value) if not, use InitialPostEditorState
        setPostSource(
          updateImmutable(old, {
            postGroups: {
              $apply: (current: PostGroupsType[]) =>
                current.map(group =>
                  updateImmutable(group, {
                    categoryId: { $set: post.category.id },
                    familyUuid: {
                      $set: postEditorContextType === PostEditorContextEnum.duplicate ? null : post.familyUuid || null
                    },
                    profiles: {
                      $set: post.socialProfile
                        ? [
                            {
                              __typename: post.socialProfile.__typename,
                              profileId: post.socialProfile.id,
                              ...(post.pinterestBoardId ? { pnBoardId: post.pinterestBoardId } : {}),
                              ...(post.tikTok ? { tikTok: mapPostTikTokToTikTokProfileValues(post.tikTok) } : {})
                            }
                          ]
                        : []
                    },
                    bitlyAccount: {
                      profilesWithBitly: { $set: profilesWithBitly },
                      defaultBitly: { $set: defaultBitly }
                    },
                    groupPostAttributes: {
                      $set: updateImmutable(mappedGroup, {
                        scheduling: {
                          evergreen: {
                            active: {
                              $apply: currentEvergrren => (overrideWorkspaceSettings ? currentEvergrren : evergreen)
                            }
                          },
                          queuePosition: {
                            $apply: currentQueuePosition =>
                              overrideWorkspaceSettings ? currentQueuePosition : queuePosition
                          },
                          scheduledTimezone: {
                            $apply: currentScheduledTimezone =>
                              overrideWorkspaceSettings ? currentScheduledTimezone : scheduledTimezone
                          }
                        },
                        content: {
                          varyMedia: {
                            $apply: currentVaryMedia => (overrideWorkspaceSettings ? currentVaryMedia : varyMedia)
                          }
                        },
                        postStatus: { $set: newStatus }
                      })
                    }
                  })
                )
            }
          }),
          sourceType
        )
      );
      setLoading(false);
    }
  }, [
    profiles,
    categories,
    workspaceSettings,
    bitlyAccounts,
    overrideWorkspaceSettings,
    data,
    postEditorContextType,
    sourceType
  ]);

  return (
    <PostEditorNewFromPostView
      loading={loading}
      initialState={postEditorState}
      postId={postId}
      profiles={profiles}
      overrideWorkspaceSettings={overrideWorkspaceSettings}
      categories={categories}
    />
  );
};
